import {
    AUTO_RECOVERY_KEY,
    INLINE_BLOCKED_RECOVERIES_GLOBAL,
    INLINE_BLOCKED_RECOVERY_EVENT,
} from 'lib/telemetry/chunkReloadScript';
import * as datadogRum from 'lib/telemetry/datadogRum';
import { noteFailure } from 'lib/telemetry/recentFailures';
import { getLoadedBuildId } from 'utils/versionSkew';

import { captureRecoveryDiagnostics, rememberRecovery, sanitizeRecoveryDiagnostics } from './recoveryDiagnostics';

/**
 * `auto_recovery` RUM action: one event per automatic recovery *attempt* made
 * by the inline chunk-reload script (chunkReloadScript.ts), the version-skew hook and
 * boundary, the transient-DOM retry hook, and importWithRetry. It records that
 * a recovery was attempted, not that the original operation eventually succeeded.
 *
 * Signal contract (consumed by the Datadog monitor below — keep in sync):
 * - `reason`: `chunk_load` | `version_skew` | `transient_dom`
 * - `source`: `inline_chunk_reload` | `version_skew_hook` | `chunk_skew_boundary`
 *   | `transient_dom_hook` | `import_with_retry`
 * - `recovery_type`: `reload` | `retry`
 * - `source_version`: the build that attempted recovery. RUM's standard
 *   `version` on the same event is the build that *reported* it, which for a
 *   reload is the fresh deploy the tab landed on.
 * - `occurred_at`: attempt time, Unix ms.
 * - `attempt` (transient DOM retries), `retries_remaining` + `delay_ms` (import
 *   retries): the retry budget at the time of the attempt.
 *
 * Reload paths can't emit before the page unloads, so they stamp a record into
 * sessionStorage (after their reload-loop guard has passed) and the next page
 * publishes it once, from `initDatadog`'s onReady, after the initial RUM view
 * has started. Consuming the record never touches the loop guards. In-page
 * retries go through the lazy RUM facade, which queues until init. Bounded,
 * sanitized diagnostics preserve the original asset/error and page context.
 * `recovery_id` and `previous_recovery_id` correlate repeated attempts. Reload
 * records report `recovery_outcome: rum_ready` and `recovery_elapsed_ms`; this
 * only proves the next page booted RUM, not that the interaction succeeded.
 * Import retries share an id and emit `auto_recovery_outcome` on completion
 * (`import_succeeded` / `import_failed`), outside the attempt monitor.
 * Pages that never boot RUM afterward remain outside this signal.
 *
 * Datadog monitor 324128387 "[Web] Prod - Automatic recovery session rate":
 * 100 * distinct sessions with an auto_recovery action / distinct active
 * sessions over 5m (warn 0.5%, alert 1%). Triage in the RUM explorer with
 *   service:trading-portal env:prod @type:action @action.name:auto_recovery
 * broken down by @context.reason / @context.source / @context.source_version.
 *
 * `auto_recovery_blocked` RUM action: a recovery was needed but its reload-loop
 * guard refused it, so the page stays broken — usually rendering nothing where
 * the failed chunk should be. Same `reason` / `source` / `source_version` /
 * `occurred_at` fields, plus `guard`:
 * - `recent_reload`: a chunk reload already happened in the last 10s.
 * - `already_reloaded_build`: this build already spent its one skew reload.
 * - `storage_unavailable`: the guard can't be persisted, so reloading could loop.
 */

// Also written by the inline chunk-reload script. Keep the wire format
// compatible: an old page can hand this record to a newer deployment.
export { AUTO_RECOVERY_KEY };
const MAX_RECORD_AGE_MS = 24 * 60 * 60 * 1000;

const RECOVERY_REASONS = ['chunk_load', 'version_skew', 'transient_dom'] as const;
const RECOVERY_SOURCES = [
    'inline_chunk_reload',
    'version_skew_hook',
    'chunk_skew_boundary',
    'transient_dom_hook',
    'import_with_retry',
] as const;

type RecoveryReason = (typeof RECOVERY_REASONS)[number];
type RecoverySource = (typeof RECOVERY_SOURCES)[number];

const BLOCKED_GUARDS = ['recent_reload', 'already_reloaded_build', 'storage_unavailable'] as const;
export type BlockedRecoveryGuard = (typeof BLOCKED_GUARDS)[number];

const isBlockedGuard = (value: unknown): value is BlockedRecoveryGuard =>
    (BLOCKED_GUARDS as readonly unknown[]).includes(value);

// A chunk that keeps failing fires on every import attempt; a few reports say as much.
const MAX_BLOCKED_REPORTS_PER_PAGE = 5;
let blockedReports = 0;

interface RecoveryRecord {
    reason: RecoveryReason;
    source: RecoverySource;
    recovery_type: 'reload' | 'retry';
    source_version: string;
    occurred_at: number;
    diagnostics?: unknown;
}

/** Persist only when a reload will actually happen, after its loop guard passes. */
export function recordReloadRecovery(
    reason: RecoveryReason,
    source: RecoverySource,
    error?: unknown,
    recoveryId?: string,
): void {
    try {
        const diagnostics = captureRecoveryDiagnostics(
            source === 'import_with_retry' ? 'import_error' : 'react_error',
            error,
        );
        if (recoveryId) diagnostics.recovery_id = recoveryId;
        const record: RecoveryRecord = {
            reason,
            source,
            recovery_type: 'reload',
            source_version: getLoadedBuildId(),
            occurred_at: Date.now(),
            diagnostics,
        };
        sessionStorage.setItem(AUTO_RECOVERY_KEY, JSON.stringify(record));
        rememberRecovery(sanitizeRecoveryDiagnostics(record.diagnostics).recovery_id);
    } catch (error) {
        // Telemetry must never prevent recovery. Storage is denied in sandboxed
        // webviews / Safari with cookies blocked, so this is a warning, not an
        // error: the reload still happens, it just won't be counted.
        console.warn('[AutoRecovery] could not stamp reload; this recovery will be missing from RUM', {
            reason,
            source,
            error,
        });
    }
}

/** In-page retries don't unload the facade's pre-init queue. */
export function reportRetryRecovery(
    reason: RecoveryReason,
    source: RecoverySource,
    details: { attempt?: number; retries_remaining?: number; delay_ms?: number },
    error?: unknown,
    recoveryId?: string,
): string | undefined {
    noteFailure('auto_recovery');
    try {
        const diagnostics = captureRecoveryDiagnostics(
            source === 'import_with_retry' ? 'import_error' : 'react_error',
            error,
        );
        if (recoveryId) diagnostics.recovery_id = recoveryId;
        datadogRum.addAction('auto_recovery', {
            reason,
            source,
            recovery_type: 'retry',
            source_version: getLoadedBuildId(),
            occurred_at: Date.now(),
            ...details,
            ...diagnostics,
        });
        return diagnostics.recovery_id as string;
    } catch (error) {
        // Observability must not break the retry it is reporting.
        console.warn('[AutoRecovery] could not report retry; this recovery will be missing from RUM', {
            reason,
            source,
            ...details,
            error,
        });
    }
}

/** Separate action so an outcome cannot increment the recovery-attempt monitor. */
export function reportImportRecoveryOutcome(
    recoveryId: string,
    startedAt: number,
    outcome: 'import_succeeded' | 'import_failed',
): void {
    try {
        datadogRum.addAction('auto_recovery_outcome', {
            recovery_id: recoveryId,
            source: 'import_with_retry',
            source_version: getLoadedBuildId(),
            recovery_outcome: outcome,
            recovery_elapsed_ms: Math.max(0, Date.now() - startedAt),
        });
    } catch (error) {
        console.warn('[AutoRecovery] could not report import outcome', { error });
    }
}

function isValidReloadRecord(record: unknown, now: number): record is RecoveryRecord {
    if (!record || typeof record !== 'object') return false;
    const { reason, source, recovery_type, source_version, occurred_at } = record as Partial<RecoveryRecord>;
    return (
        recovery_type === 'reload' &&
        (RECOVERY_REASONS as readonly string[]).includes(reason as string) &&
        (RECOVERY_SOURCES as readonly string[]).includes(source as string) &&
        typeof source_version === 'string' &&
        source_version.length <= 200 &&
        typeof occurred_at === 'number' &&
        Number.isFinite(occurred_at) &&
        now - occurred_at >= 0 &&
        now - occurred_at <= MAX_RECORD_AGE_MS
    );
}

/** Call on the real SDK only after init AND the initial RUM view has started. */
export function flushReloadRecovery(addAction: typeof datadogRum.addAction): void {
    let raw: string | null = null;
    try {
        raw = sessionStorage.getItem(AUTO_RECOVERY_KEY);
        if (!raw) return;
        // Consume before publishing so repeat init cannot count the reload twice.
        // This key is separate from (and must never clear) the reload-loop guards.
        sessionStorage.removeItem(AUTO_RECOVERY_KEY);
        const record: unknown = JSON.parse(raw);
        if (!isValidReloadRecord(record, Date.now())) {
            // Expected for a tab abandoned for >24h; otherwise the wire format
            // written by chunkReloadScript.ts and this module has drifted.
            console.warn('[AutoRecovery] dropping stale or malformed reload record');
            return;
        }
        noteFailure('auto_recovery');
        // Allowlist fields from storage; never replay arbitrary context/PII.
        addAction('auto_recovery', {
            reason: record.reason,
            source: record.source,
            recovery_type: record.recovery_type,
            source_version: record.source_version,
            occurred_at: record.occurred_at,
            ...sanitizeRecoveryDiagnostics(record.diagnostics),
            recovery_outcome: 'rum_ready',
            recovery_elapsed_ms: Math.max(0, Date.now() - record.occurred_at),
        });
    } catch (error) {
        // Denied storage, unparseable JSON, or an SDK failure: best-effort only,
        // and the page must keep booting.
        console.warn('[AutoRecovery] could not publish reload record; this recovery will be missing from RUM', {
            error,
        });
    }
}

/** A recovery its loop guard refused. The page is left as it is, so this is the last trace of it. */
export function reportBlockedRecovery(
    reason: RecoveryReason,
    source: RecoverySource,
    guard: BlockedRecoveryGuard,
    occurredAt = Date.now(),
): void {
    noteFailure('recovery_blocked');
    if (blockedReports >= MAX_BLOCKED_REPORTS_PER_PAGE) return;
    blockedReports++;
    try {
        datadogRum.addAction('auto_recovery_blocked', {
            reason,
            source,
            guard,
            source_version: getLoadedBuildId(),
            occurred_at: occurredAt,
        });
    } catch (error) {
        console.warn('[AutoRecovery] could not report a blocked recovery; it will be missing from RUM', {
            reason,
            source,
            guard,
            error,
        });
    }
}

function drainInlineBlockedRecoveries(): void {
    const queued: unknown = (window as unknown as Record<string, unknown>)[INLINE_BLOCKED_RECOVERIES_GLOBAL];
    if (!Array.isArray(queued)) return;
    for (const entry of queued.splice(0) as unknown[]) {
        const { guard, occurred_at } = (entry ?? {}) as { guard?: unknown; occurred_at?: unknown };
        if (!isBlockedGuard(guard)) {
            console.warn('[AutoRecovery] dropping malformed blocked-recovery entry from the inline script', { entry });
            continue;
        }
        reportBlockedRecovery(
            'chunk_load',
            'inline_chunk_reload',
            guard,
            typeof occurred_at === 'number' ? occurred_at : Date.now(),
        );
    }
}

/**
 * The inline chunk-reload script queues its blocked reloads on a window global
 * and fires an event, since it runs before any app chunk. Drain what queued up
 * before this ran, then keep listening. Safe to repeat: the listener is the same
 * function, so the browser dedupes it.
 */
export function installInlineBlockedRecoveryBridge(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener(INLINE_BLOCKED_RECOVERY_EVENT, drainInlineBlockedRecoveries);
    drainInlineBlockedRecoveries();
}
